import React, { useEffect, useMemo, useState } from 'react';
import { Chat, Preview } from '@matap/shared';
import { AppTextView } from '@/components/base/app.text.view';
import { AppImageView } from '@/components/base/app.image.view';
import { useScreenSize } from '@/hooks/use.screen.size';
import { Box } from '@/components/base/box';
import styled from 'styled-components/native';
import { Image } from 'expo-image';
import { ChatFooterView } from './chat.footer.view';
import { ChatContainer } from './chat.container';
import { AppCardView } from '@/components/base/app.card.view';
import { scaleImage, wpVal } from '../../../utils';
import { initFactory } from '../../../utils/job.manager';

const imageFetchFactory = initFactory('image-fetch-factory', { capacity: 50, rateLimit: 1, maxCapPolicy: 'drop_oldest' });

interface Props {
  chat: Chat;
  isSentByUser: boolean;
  temporary: boolean;
  onPress?: (e: any) => void; // <-- Keep onPress
  onLongPress?: (e: any) => void; // <-- Restored onLongPress
}

function BaseChatTextView({ chat, isSentByUser, onPress, onLongPress }: Props) {
  return (
    <Wrapper isSentByUser={isSentByUser}>
      <Text
        onPress={onPress} // Pass onPress
        onLongPress={onLongPress} // Pass onLongPress
        hyperLink
        textColor={isSentByUser ? 'black_light' : 'black_light'}
        text={chat.text}
        variant={'chat'}
      />
      {chat.preview && <PreviewComponent preview={chat.preview} />}
      <ChatFooterView chat={chat} isSentByUser={isSentByUser} />
    </Wrapper>
  );
}

// ... (PreviewComponent and styled components remain exactly the same)
function PreviewComponent({ preview }: { preview: Preview }) {
  const { wp } = useScreenSize();
  const [image, setImage] = useState<string>();
  const [baseImageSize, setBaseImageSizes] = useState<{ width: number; height: number } | undefined>();

  const scaledStyle = useMemo(() => {
    if (!baseImageSize) {
      return undefined;
    }
    return scaleImage(baseImageSize.width, baseImageSize.height, Math.min(wp(60), 400), Math.min(wp(60), 400));
  }, [baseImageSize, wp]);

  useEffect(() => {
    let isMounted = true;
    setImage(undefined);
    const img = preview.image;
    if (!img) return;

    imageFetchFactory.schedule(() => {
      Image.prefetch(img, { cachePolicy: 'memory' })
        .then((res) => {
          if (isMounted && res) setImage(img);
        })
        .catch(console.error);
    });

    return () => {
      isMounted = false;
    };
  }, [preview.image]);

  return (
    <PreviewContainer shadowColor={'#000'} shadowOpacity={0.09} blurRadius={6} shadowOffsetX={0} shadowOffsetY={3}>
      <Divider fullWidth height={1} />
      <PreviewContent fullWidth>
        <PreviewText bold>{preview.title}</PreviewText>
        {preview.description ? <PreviewText>{preview.description}</PreviewText> : undefined}
      </PreviewContent>
      {image && (
        <ImageContainer fullWidth alignItems={'center'}>
          <PreviewImage
            source={{ uri: image }}
            onLoad={(e) => {
              setBaseImageSizes({ width: e.source.width, height: e.source.height });
            }}
            style={scaledStyle}
          />
        </ImageContainer>
      )}
    </PreviewContainer>
  );
}

const Text = styled(AppTextView)``;

const Wrapper = styled(ChatContainer)`
  max-width: ${wpVal(60)};
`;

const PreviewText = styled(AppTextView)``;

const PreviewContainer = styled(AppCardView)`
  background-color: ${({ theme }) => theme.colors.greyWhite};
  margin-top: 10px;
  border-radius: 10px;
  padding: 8px;
`;
const Divider = styled(Box)`
  background-color: transparent;
`;
const PreviewContent = styled(Box)`
  margin-top: 4px;
`;
const ImageContainer = styled(Box)`
  margin-top: 8px;
  align-items: center;
`;
const PreviewImage = styled(AppImageView)`
  min-width: 120px;
  min-height: 80px;
  max-width: 420px;
  max-height: 300px;
  border-radius: 8px;
  overflow: hidden;
  shadow-color: #000;
  shadow-opacity: 0.06;
  shadow-radius: 8px;
  shadow-offset: 0px 4px;
  elevation: 3;
`;

export const ChatTextView = React.memo(BaseChatTextView);
